import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { ConsultarCotizacionGuardadaResponse } from '../../models/cotizador-cotizacion/consultar-cotizacion-guardada/response/consultar-cotizacion-guardada-response.model';
import {
  ConsultarDescuentosProntoPagoParams,
  ConsultarDescuentosProntoPagoRequest
} from '../../models/cotizador-cotizacion/consultar-descuentos-pronto-pago/request/consultar-descuentos-pronto-pago-request.model';
import { ConsultarDescuentosProntoPagoResponse } from '../../models/cotizador-cotizacion/consultar-descuentos-pronto-pago/response/consultar-descuentos-pronto-pago-response.model';
import { ConsultarCampaniaDetalleHttpResponse } from '../../models/cotizador-cotizacion/consultar-campania-detalle/response/consultar-campania-detalle-response.model';
import {
  ConsultarCuotaInscripcionParams,
  ConsultarCuotaInscripcionRequest
} from '../../models/cotizador-cotizacion/consultar-cuota-inscripcion/request/consultar-cuota-inscripcion-request.model';
import { ConsultarCuotaInscripcionResponse } from '../../models/cotizador-cotizacion/consultar-cuota-inscripcion/response/consultar-cuota-inscripcion-response.model';
import { 
  PorcentajeDescuentoSpParams, 
  PorcentajeDescuentoSpRequest 
} from '../../models/cotizador-cotizacion/consultar-descuentos-pronto-pago/request/consultar-porcentaje-descuento-request.model';
import { 
  PorcentajeDescuentoSpResponse 
} from '../../models/cotizador-cotizacion/consultar-descuentos-pronto-pago/response/consultar-porcentaje-descuento-response.model';
import {
  ConsultarGrupoProgramaXProgramaParams,
  ConsultarGrupoProgramaXProgramaRequest
} from '../../models/cotizador-cotizacion/consultar-grupo-programa-x-programa/request/consultar-grupo-programa-x-programa-request.model';
import { ConsultarGrupoProgramaXProgramaResponse } from '../../models/cotizador-cotizacion/consultar-grupo-programa-x-programa/response/consultar-grupo-programa-x-programa-response.model';
import { URL_MAP } from '../../shared/urlMap';
import { environment } from '../../../environments/environment';

export interface ConsultarCotizacionComparativoSeleccionItem {
  lineaNegocio: string;
  programa1: string;
  programa2: string;
}

export interface ConsultarCotizacionComparativoResponse {
  status: boolean;
  codeStatus: string;
  message: string;
  data?: {
    idCotizacion: number;
    comparativos: ConsultarCotizacionComparativoSeleccionItem[];
  } | null;
  errorMessage?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ConsultarCotizacionService {
  private readonly cotizacionBaseUrl = `${environment.HOST}${URL_MAP.API.COTIZACION}`;
  private readonly campaniaDetalleUrl = `${environment.HOST}${URL_MAP.API.CONSULTAR_CAMPANIA_DETALLE}`;
  private readonly descuentosProntoPagoUrl = `${environment.HOST}${URL_MAP.API.CONSULTAR_DESCUENTOS_PRONTO_PAGO}`;
  private readonly grupoProgramaXProgramaUrl = `${environment.HOST}${URL_MAP.API.CONSULTAR_GRUPO_PROGRAMA_X_PROGRAMA}`;
  private readonly cuotaInscripcionUrl = `${environment.HOST}${URL_MAP.API.CONSULTAR_CUOTA_INSCRIPCION}`;
  private readonly cotizacionComparativoUrl = `${environment.HOST}${URL_MAP.API.CONSULTAR_COTIZACION_COMPARATIVO}`;

  constructor(private http: HttpClient) { }

  consultarCotizacionGuardada(idCotizacion: number | string): Observable<ConsultarCotizacionGuardadaResponse> {
    return this.http.get<ConsultarCotizacionGuardadaResponse>(`${this.cotizacionBaseUrl}/${idCotizacion}/edit`);
  }

  consultarCotizacionComparativo(idCotizacion: number | string): Observable<ConsultarCotizacionComparativoResponse> {
    return this.http.get<ConsultarCotizacionComparativoResponse>(this.cotizacionComparativoUrl, {
      params: { idCotizacion: String(idCotizacion) }
    });
  }

  /**
   * RQ005: detalle de descuentos por mes (COTIZADOR_CAMPANIA_DETALLE) vía id de COTIZACION_CAMPANIA.
   */
  consultarCampaniaDetalle(idCotizacionCampania: number): Observable<ConsultarCampaniaDetalleHttpResponse> {
    return this.http.get<ConsultarCampaniaDetalleHttpResponse>(this.campaniaDetalleUrl, {
      params: { idCotizacionCampania: String(idCotizacionCampania) }
    });
  }

  consultarDescuentosProntoPago(
    params: ConsultarDescuentosProntoPagoParams
  ): Observable<ConsultarDescuentosProntoPagoResponse> {
    const request: ConsultarDescuentosProntoPagoRequest = {
      nombreClave: 'ConsultarDescProntoPago',
      jsonParametros: JSON.stringify({
        tipoSolicitud: String(params.tipoSolicitud),
        tipoPlan: String(params.tipoPlan ?? ''),
        lineaNegocio: String(params.lineaNegocio),
        programa: String(params.programa ?? ''),
        cantidadMeses: String(params.cantidadMeses)
      })
    };

    return this.http
      .post<ConsultarDescuentosProntoPagoResponse>(this.descuentosProntoPagoUrl, request)
      .pipe(
        catchError((error) => {
          console.error('Error en consultarDescuentosProntoPago:', error);
          return throwError(() => new Error(error?.message || 'Error al consultar descuentos de pronto pago'));
        })
      );
  }


  consultarPorcentajeDescuentoSp(
    params: PorcentajeDescuentoSpParams
  ): Observable<PorcentajeDescuentoSpResponse> {
    const request: PorcentajeDescuentoSpRequest = {
      nombreClave: 'getSpQPorcentajeDescuento',
      jsonParametros: JSON.stringify({
        P_LINEA_NEGOCIO: String(params.lineaNegocio).toUpperCase().trim(),
        P_MESES: String(params.meses).trim(),
        P_PLAN: String(params.plan).toUpperCase().trim(),
        P_GRUPO_PROGRAMA: String(params.grupoPrograma ?? '').toUpperCase().trim(),
        P_SUCURSAL: String(params.sucursal ?? '').toUpperCase().trim()
      })
    };

    return this.http
      .post<PorcentajeDescuentoSpResponse>(this.descuentosProntoPagoUrl, request)
      .pipe(
        catchError((error) => {
          console.error('Error crítico en consultarPorcentajeDescuentoSp:', error);
          return throwError(() => new Error(error?.message || 'Error al invocar SP de porcentajes de descuento'));
        })
      );
  }

  /**
   * Resuelve grupo_programa a partir del código de programa (getGrupoProgramaxPrograma).
   * Usado para armar P_GRUPO_PROGRAMA en getSpQPorcentajeDescuento.
   */
  consultarGrupoProgramaXPrograma(
    params: ConsultarGrupoProgramaXProgramaParams
  ): Observable<ConsultarGrupoProgramaXProgramaResponse> {
    const request: ConsultarGrupoProgramaXProgramaRequest = {
      nombreClave: 'getGrupoProgramaxPrograma',
      jsonParametros: JSON.stringify({
        programa: String(params.programa ?? '').toUpperCase().trim()
      })
    };

    return this.http
      .post<ConsultarGrupoProgramaXProgramaResponse>(this.grupoProgramaXProgramaUrl, request)
      .pipe(
        catchError((error) => {
          console.error('Error en consultarGrupoProgramaXPrograma:', error);
          return throwError(() => new Error(error?.message || 'Error al consultar grupo de programa'));
        })
      );
  }

  consultarCuotaInscripcion(
    params: ConsultarCuotaInscripcionParams
  ): Observable<ConsultarCuotaInscripcionResponse> {
    const request: ConsultarCuotaInscripcionRequest = {
      nombreClave: 'getCuota',
      jsonParametros: JSON.stringify({
        P_ID_TIPO_SOLICITUD: String(params.idTipoSolicitud),
        P_LINEA_NEGOCIO: String(params.lineaNegocio),
        P_PLAN: String(params.plan ?? ''),
        P_GRUPO_PROGRAMA: String(params.grupoPrograma ?? '').toUpperCase().trim(),
        P_TIPO_CUOTA: '',
        P_APLICACION_CUOTA: '',
        P_TIPO_COBERTURA: '',
        P_EDAD: params.edad != null && String(params.edad).trim() !== ''
          ? String(params.edad).trim()
          : '',
        P_GENERO: params.genero != null && String(params.genero).trim() !== ''
          ? String(params.genero).trim().toUpperCase()
          : ''
      })
    };

    return this.http
      .post<ConsultarCuotaInscripcionResponse>(this.cuotaInscripcionUrl, request)
      .pipe(
        catchError((error) => {
          console.error('Error en consultarCuotaInscripcion:', error);
          return throwError(() => new Error(error?.message || 'Error al consultar cuota de inscripción'));
        })
      );
  }
}
